import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, IdCard, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { SituationBadge } from "./Badges";
import { RegisterPaymentForm } from "./RegisterPayment";
import { api, brl, dateBR, formatError, maskCPF, monthLabel } from "../../lib/api";

const METHOD = { pix: "Pix", card: "Cartão", dinheiro: "Dinheiro" };

function Info({ label, value }) {
  return (
    <div>
      <p className="text-neutral-500 text-xs">{label}</p>
      <p className="font-medium break-all">{value || "—"}</p>
    </div>
  );
}

function PaymentsTable({ payments, onDelete, onConfirm }) {
  if (!payments.length) return <p className="text-sm text-neutral-500">Nenhum pagamento registrado.</p>;
  return (
    <div className="overflow-x-auto max-h-60">
      <table className="w-full text-sm" data-testid="detail-payments-table">
        <thead className="text-left text-neutral-500">
          <tr>
            <th className="py-2 font-medium">Pago em</th>
            <th className="py-2 font-medium">Cobre até</th>
            <th className="py-2 font-medium">Método</th>
            <th className="py-2 font-medium">Valor</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p.id} className="border-t border-neutral-100">
              <td className="py-2 font-semibold">{p.status === "paid" ? dateBR(p.paid_at) : <span className="text-amber-700">Aguardando</span>}</td>
              <td className="py-2">{dateBR(p.covers_until)}</td>
              <td className="py-2">{METHOD[p.method] || p.method}</td>
              <td className="py-2">{brl(p.amount)}</td>
              <td className="py-2 text-right whitespace-nowrap">
                {p.status === "awaiting" && (
                  <button onClick={() => onConfirm(p)} className="text-[#166534] hover:text-[#052E16] mr-3 text-xs font-semibold" data-testid={`detail-confirm-${p.id}`}>
                    Confirmar
                  </button>
                )}
                <button onClick={() => onDelete(p)} className="text-rose-600 hover:text-rose-800" data-testid={`delete-payment-${p.id}`} title="Estornar">
                  <Trash2 className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MemberDetailDialog({ memberId, onClose, onChanged, onCard }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/members/${memberId}`);
      setData(data);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, [memberId]);

  useEffect(() => {
    if (memberId) load();
    else setData(null);
  }, [memberId, load]);

  async function refreshAll() {
    await load();
    onChanged();
  }

  async function confirmPayment(p) {
    try {
      await api.post(`/admin/payments/${p.id}/confirm`);
      toast.success("Recebimento confirmado");
      refreshAll();
    } catch (err) {
      toast.error(formatError(err));
    }
  }

  async function deletePayment(p) {
    if (!window.confirm(`${p.status === "awaiting" ? "Recusar" : "Estornar"} este pagamento?`)) return;
    try {
      await api.delete(`/admin/payments/${p.id}`);
      toast.success("Pagamento estornado");
      refreshAll();
    } catch (err) {
      toast.error(formatError(err));
    }
  }

  const m = data?.member;
  return (
    <Dialog open={!!memberId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="member-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {m?.nome_completo} <span className="text-[#166534] font-mono text-base">{m?.cadastro_number}</span>
            {m && <SituationBadge member={m} />}
          </DialogTitle>
          <DialogDescription>Dados, situação e histórico de mensalidades</DialogDescription>
        </DialogHeader>
        {m && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <Info label="E-mail" value={m.email} />
              <Info label="Telefone" value={m.telefone} />
              <Info label="CPF" value={maskCPF(m.cpf || "")} />
              <Info label="Nascimento" value={dateBR(m.data_nascimento)} />
              <Info label="Adesão" value={dateBR(m.created_at)} />
              <Info label="Último pagamento" value={m.last_payment_at ? dateBR(m.last_payment_at) : "—"} />
              <Info label="Próximo vencimento" value={m.next_due_date ? dateBR(m.next_due_date) : "Após 1º pagamento confirmado"} />
            </div>
            <Button variant="outline" size="sm" onClick={() => onCard(m)} data-testid="detail-open-card">
              <IdCard className="w-4 h-4 mr-2" /> Carteirinha digital
            </Button>
            {m.situation === "overdue" && (
              <div className="rounded-xl bg-rose-50 border border-rose-200 p-4" data-testid="detail-overdue">
                <p className="flex items-center gap-2 text-sm font-semibold text-rose-800">
                  <AlertCircle className="w-4 h-4" /> Vencido desde {dateBR(m.next_due_date)} ({m.days_overdue} dia(s))
                </p>
              </div>
            )}
            <div>
              <p className="font-semibold mb-2 text-sm">Registrar pagamento</p>
              <RegisterPaymentForm memberId={m.id} onDone={refreshAll} />
            </div>
            <div>
              <p className="font-semibold mb-2 text-sm">Histórico de pagamentos</p>
              <PaymentsTable payments={data.payments} onDelete={deletePayment} onConfirm={confirmPayment} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
